<template>
    <v-container grid-list-md pa-0 relative>
        <v-layout row wrap>
            <v-flex xs12>
                <v-card outlined class="primary--border">
                    <v-card-title class="title">
                        Sales Book Report

                        <v-spacer />

                        <print-buttons @action="download" v-if="records.length" :passAuthToReport="true" :pdf="false"
                            :excel="true" labelExcel="Download Excel" labelPdf="Download PDF" />
                    </v-card-title>

                    <v-card outlined>
                        <v-card-title class="title mt-3">
                            <v-flex xs12 sm3 md2>
                                <v-select v-model="year" :items="yearItems" label="Year" outlined dense hide-details />
                            </v-flex>

                            <v-flex xs12 sm3 md2>
                                <v-select v-model="month" :items="monthItems" label="Month" outlined dense
                                    hide-details />
                            </v-flex>

                            <v-flex xs12 sm2 md1>
                                <search-button :disabled="loading" permission="receipt-read" @action="search">
                                    Search
                                </search-button>
                            </v-flex>
                        </v-card-title>

                        <div v-if="records.length" class="data-represent" style="margin-top: -10px;">
                            <div>
                                <span class="d-info"></span>
                                &nbsp;

                                <small>
                                    Showing
                                    <strong>{{ records.length }}</strong>
                                    of
                                    <strong>{{ totalItems }}</strong>
                                    bill(s)
                                </small>
                            </div>

                            <div>
                                <span class="d-primary"></span>
                                &nbsp;

                                <small>
                                    Period:
                                    <strong>{{ periodLabel }}</strong>
                                </small>
                            </div>
                        </div>

                        <br />
                    </v-card>

                    <v-data-table :headers="headers" :items="records" :options.sync="pagination" :footer-props="{
                        ...footerProps,
                        itemsPerPageOptions: [10, 25, 50, 100]
                    }" :server-items-length="totalItems" :loading="loading" hide-default-header
                        class="sales-book-table">
                        <!-- The IRD sales book uses a grouped two row header. -->
                        <template v-slot:header>
                            <thead class="v-data-table-header">
                                <tr>
                                    <th colspan="4" class="text-center group-cell">Invoice</th>
                                    <th rowspan="2" class="text-center group-cell">Total Sales</th>
                                    <th rowspan="2" class="text-center group-cell">Non Taxable Sales</th>
                                    <th rowspan="2" class="text-center group-cell">Export Sales</th>
                                    <th rowspan="2" class="text-center group-cell">Discount</th>
                                    <th colspan="2" class="text-center group-cell">Taxable Sales</th>
                                </tr>

                                <tr>
                                    <th class="text-center group-cell">Date</th>
                                    <th class="text-center group-cell">Bill No</th>
                                    <th class="text-center group-cell">Buyer's Name</th>
                                    <th class="text-center group-cell">Buyer's PAN Number</th>
                                    <th class="text-center group-cell">Amount</th>
                                    <th class="text-center group-cell">Tax(Rs)</th>
                                </tr>
                            </thead>
                        </template>

                        <template v-slot:item="{ item }">
                            <tr>
                                <td class="text-left">
                                    {{ billDate(item) || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ item.bill_no || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ buyerName(item) || "-" }}
                                </td>

                                <td class="text-left">
                                    {{ buyerPan(item) || "-" }}
                                </td>

                                <td class="text-right">
                                    {{ amount(totalSales(item)) }}
                                </td>

                                <td class="text-right">
                                    {{ amount(nonTaxableSales(item)) }}
                                </td>

                                <td class="text-right">
                                    {{ amount(exportSales(item)) }}
                                </td>

                                <td class="text-right">
                                    {{ amount(discount(item)) }}
                                </td>

                                <td class="text-right">
                                    {{ amount(taxableAmount(item)) }}
                                </td>

                                <td class="text-right">
                                    {{ amount(taxAmount(item)) }}
                                </td>
                            </tr>
                        </template>

                        <template v-slot:body.append>
                            <tr v-if="records.length" class="total-row">
                                <td colspan="4" class="text-center">
                                    <strong>Total amount</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.totalSales) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.nonTaxableSales) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.exportSales) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.discount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.taxableAmount) }}</strong>
                                </td>

                                <td class="text-right">
                                    <strong>{{ amount(totals.taxAmount) }}</strong>
                                </td>
                            </tr>
                        </template>

                        <template v-slot:no-data>
                            <div class="pa-8 text-center grey--text">
                                No sales found for the selected month.
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-flex>
        </v-layout>
    </v-container>
</template>

<script>
import Mixins from "@/library/Mixins";
import { v2Services } from "@/library/helpers";

const NepaliDate = require("nepali-date-js");
const nd = new NepaliDate();

const MONTHS = [
    "Baishakh",
    "Jestha",
    "Ashadh",
    "Shrawan",
    "Bhadra",
    "Ashwin",
    "Kartik",
    "Mangsir",
    "Poush",
    "Magh",
    "Falgun",
    "Chaitra",
];

export default {
    name: "SalesBookReport",

    mixins: [Mixins],

    data: () => ({
        records: [],
        loading: false,
        downloading: false,
        requestInitialized: false,

        year: nd.getYear(),
        month: nd.getMonth() + 1,

        totalItems: 0,

        pagination: {
            page: 1,
            itemsPerPage: 50,
            sortBy: [],
            sortDesc: [],
        },

        monthItems: MONTHS.map((name, index) => ({
            text: name,
            value: index + 1,
        })),

        headers: [
            { text: "Date", value: "bill_date", width: 120, sortable: false },
            { text: "Bill No", value: "bill_no", width: 180, sortable: false },
            {
                text: "Buyer's Name",
                value: "customer_name",
                width: 220,
                sortable: false,
            },
            {
                text: "Buyer's PAN Number",
                value: "customer_pan",
                width: 170,
                sortable: false,
            },
            {
                text: "Total Sales",
                value: "total_amount",
                width: 140,
                sortable: false,
            },
            {
                text: "Non Taxable Sales",
                value: "non_taxable_amount",
                width: 160,
                sortable: false,
            },
            {
                text: "Export Sales",
                value: "export_amount",
                width: 140,
                sortable: false,
            },
            {
                text: "Discount",
                value: "discount",
                width: 130,
                sortable: false,
            },
            {
                text: "Amount",
                value: "taxable_amount",
                width: 150,
                sortable: false,
            },
            {
                text: "Tax(Rs)",
                value: "tax_amount",
                width: 130,
                sortable: false,
            },
        ],
    }),

    computed: {
        yearItems() {
            const current = nd.getYear();
            const years = [];

            for (let year = current + 1; year >= current - 4; year--) {
                years.push({ text: String(year), value: year });
            }

            return years;
        },

        periodLabel() {
            return `${MONTHS[this.month - 1]} ${this.year}`;
        },

        totals() {
            return this.records.reduce(
                (carry, item) => ({
                    totalSales:
                        carry.totalSales + this.toNumber(this.totalSales(item)),
                    nonTaxableSales:
                        carry.nonTaxableSales +
                        this.toNumber(this.nonTaxableSales(item)),
                    exportSales:
                        carry.exportSales + this.toNumber(this.exportSales(item)),
                    discount: carry.discount + this.toNumber(this.discount(item)),
                    taxableAmount:
                        carry.taxableAmount +
                        this.toNumber(this.taxableAmount(item)),
                    taxAmount:
                        carry.taxAmount + this.toNumber(this.taxAmount(item)),
                }),
                {
                    totalSales: 0,
                    nonTaxableSales: 0,
                    exportSales: 0,
                    discount: 0,
                    taxableAmount: 0,
                    taxAmount: 0,
                }
            );
        },
    },

    watch: {
        pagination: {
            deep: true,
            handler(newValue, oldValue) {
                if (!this.requestInitialized) {
                    this.requestInitialized = true;
                    this.get();
                    return;
                }

                if (!oldValue) {
                    return;
                }

                const pageChanged = newValue.page !== oldValue.page;
                const perPageChanged =
                    newValue.itemsPerPage !== oldValue.itemsPerPage;

                if (pageChanged || perPageChanged) {
                    this.get();
                }
            },
        },
    },

    methods: {
        search() {
            if (this.pagination.page !== 1) {
                this.pagination.page = 1;
                return;
            }

            this.get();
        },

        buildFilterParams() {
            const params = new URLSearchParams();

            params.append("month", this.month);
            params.append("year", this.year);
            params.append("format", "sales-register");

            return params;
        },

        get() {
            const params = this.buildFilterParams();

            params.append("page", this.pagination.page || 1);
            params.append("perPage", this.pagination.itemsPerPage || 50);

            this.loading = true;
            this.records = [];
            this.totalItems = 0;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/reports/ird-bill-schedule?${params.toString()}`)
                .then((response) => {
                    const payload = response.data || {};
                    const records = Array.isArray(payload.data)
                        ? payload.data
                        : [];

                    this.records = records;
                    this.totalItems = payload.meta
                        ? Number(payload.meta.total || 0)
                        : records.length;
                })
                .catch(() => {
                    this.records = [];
                    this.totalItems = 0;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        download() {
            if (this.downloading) {
                return;
            }

            const params = this.buildFilterParams();

            this.downloading = true;

            this.$restV2(v2Services.BILLING)
                .get(`/v2/reports/ird-bill-schedule/download?${params.toString()}`, {
                    responseType: "blob",
                })
                .then((response) => {
                    const blob = new Blob([response.data], {
                        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = `sales-book-${this.year}-${String(
                        this.month
                    ).padStart(2, "0")}.xlsx`;
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                })
                .finally(() => {
                    this.downloading = false;
                });
        },

        /**
         * The sales-register format renames most columns, so each field is
         * resolved against that shape first and the plain bill schedule shape
         * second.
         */
        pick(item, keys) {
            for (const key of keys) {
                if (item[key] !== undefined && item[key] !== null) {
                    return item[key];
                }
            }

            return null;
        },

        billDate(item) {
            return this.pick(item, ["date", "bill_date"]);
        },

        buyerName(item) {
            return this.pick(item, ["buyer_name", "customer_name"]);
        },

        buyerPan(item) {
            return this.pick(item, [
                "buyer_pan_number",
                "buyer_pan",
                "customer_pan",
            ]);
        },

        totalSales(item) {
            return this.pick(item, ["total_sales", "total_amount"]) || 0;
        },

        nonTaxableSales(item) {
            return (
                this.pick(item, ["non_taxable_sales", "non_taxable_amount"]) || 0
            );
        },

        exportSales(item) {
            return this.pick(item, ["export_sales", "export_amount"]) || 0;
        },

        discount(item) {
            return this.pick(item, ["discount"]) || 0;
        },

        taxableAmount(item) {
            return (
                this.pick(item, ["taxable_sales_amount", "taxable_amount"]) || 0
            );
        },

        taxAmount(item) {
            return this.pick(item, ["tax_amount", "tax"]) || 0;
        },

        toNumber(value) {
            const number = parseFloat(value);

            return Number.isNaN(number) ? 0 : number;
        },

        amount(value) {
            return new Intl.NumberFormat("en-NP", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(this.toNumber(value));
        },
    },
};
</script>

<style lang="scss" scoped>
.sales-book-table {
    width: 100%;
}

.sales-book-table ::v-deep .v-data-table__wrapper {
    overflow-x: auto;
}

.sales-book-table ::v-deep table {
    min-width: 1540px;
}

.sales-book-table ::v-deep th.group-cell {
    white-space: nowrap;
    font-weight: 700;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.87);
    border: 1px solid #d5d5d5 !important;
}

.sales-book-table ::v-deep td {
    white-space: nowrap;
    border-right: 1px solid #eee;
}

.sales-book-table ::v-deep .total-row {
    background-color: #fafafa;
}

.sales-book-table ::v-deep .total-row td {
    border-top: 2px solid #ddd !important;
}
</style>
